import {UserId} from 'records/User';
import {
  AxesRanges,
  AxisMetricRangeLabel,
  AxisState,
  DataPoint,
  MetricData,
} from '@store/@types/metric';
import {IdMap} from '@store/@types/util';
import {KpiTarget} from '@store/@types/kpi';
import {formatMetric, prepareRange} from '@store/metrics-trends/metrics-trends.utils';

// Assumes null values have been filtered, and list is non-empty
export function getEarliest(data: DataPoint[]): DataPoint {
  return data.reduce((earliest, point) => {
    if (earliest.date.isAfter(point.date)) return point;
    return earliest;
  });
}

// Assumes null values have been filtered, and list is non-empty
export function getLatest(data: DataPoint[]): DataPoint {
  return data.reduce((latest, point) => {
    if (latest.date.isBefore(point.date)) return point;
    return latest;
  });
}

// Finds the earliest and latest points for all reps and returns a range
// based on these points.
function getMinMax(data: MetricData): [number, number] {
  const repData = Object.values(data);
  const allRepPoints = repData
    .map(points => {
      const data = points?.data.filter(p => p.value !== null) as DataPoint[];
      if (!data || data.length === 0) return null;

      return [getEarliest(data).value, getLatest(data).value];
    })
    .flat()
    .filter(d => d !== null) as number[];

  return [Math.min(...allRepPoints), Math.max(...allRepPoints)];
}

// Returns min and max values to render on our graph
export function getRange(
  horizontal: AxisState,
  vertical: AxisState,
  kpisById: IdMap<KpiTarget>
): AxesRanges | undefined {
  const [xMin, xMax] = getMinMax(horizontal.data);
  const [yMin, yMax] = getMinMax(vertical.data);

  return {
    xRange: prepareRange({MIN: xMin, MAX: xMax}, horizontal.metric, kpisById),
    yRange: prepareRange({MIN: yMin, MAX: yMax}, vertical.metric, kpisById),
  };
}

export function getGraphRangeLabels(
  repId: UserId | undefined,
  horizontal: AxisState,
  vertical: AxisState,
  kpisById: IdMap<KpiTarget>
): AxisMetricRangeLabel | undefined {
  const range = getRange(horizontal, vertical, kpisById);
  if (!range) return;

  return {
    xRange: {
      start: formatMetric(range.xRange.MIN, horizontal.metric, kpisById),
      end: formatMetric(range.xRange.MAX, horizontal.metric, kpisById),
    },
    yRange: {
      start: formatMetric(range.yRange.MIN, vertical.metric, kpisById),
      end: formatMetric(range.yRange.MAX, vertical.metric, kpisById),
    },
  };
}
